<template>
  <div class="god-intervention-panel">
    <!-- Horizontal panel with buttons and loading indicator inline -->
    <div class="intervention-controls">
      <div class="gm-label panel-label">God Powers:</div>

      <!-- Instant Kill Button -->
      <button
          :disabled="isLoading"
          class="gm-btn gm-btn--sm gm-btn--danger intervention-button"
          title="Instant Kill"
          @click="handleInstantKill"
      >
        <span class="intervention-icon">💀</span>
      </button>

      <!-- Instant Heal Button -->
      <button
          :disabled="isLoading"
          class="gm-btn gm-btn--sm intervention-button heal-button"
          title="Instant Heal"
          @click="handleInstantHeal"
      >
        <span class="intervention-icon">❤️</span>
      </button>

      <!-- Custom Intervention Button -->
      <button
          :disabled="isLoading"
          class="gm-btn gm-btn--sm intervention-button custom-button"
          title="Custom Intervention"
          @click="showCustomDialog = true"
      >
        <span class="intervention-icon">⚡</span>
      </button>

      <!-- Inline loading indicator -->
      <div v-if="isLoading" class="loading-indicator">
        <span class="dx-loader dx-loader--xs"></span>
      </div>
    </div>

    <!-- Success/Error message -->
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>

    <!-- Custom Intervention Dialog -->
    <div v-if="showCustomDialog" class="custom-dialog-overlay" @click.self="showCustomDialog = false">
      <div class="gm-panel gm-panel--raised custom-dialog">
        <h3 class="gm-title dialog-title">Custom God Intervention</h3>

        <div class="gm-field">
          <label class="gm-label">Type:</label>
          <div class="radio-group">
            <label>
              <input
                  v-model="customIntervention.type"
                  :value="GodInterventionType.BLESSING"
                  type="radio"
              >
              Blessing
            </label>
            <label>
              <input
                  v-model="customIntervention.type"
                  :value="GodInterventionType.CURSE"
                  type="radio"
              >
              Curse
            </label>
          </div>
        </div>

        <div class="gm-field">
          <label class="gm-label">Size:</label>
          <select v-model="customIntervention.size" class="gm-select">
            <option :value="GodInterventionSize.SMALL">Small</option>
            <option :value="GodInterventionSize.MEDIUM">Medium</option>
            <option :value="GodInterventionSize.LARGE">Large</option>
            <option :value="GodInterventionSize.GOD">God</option>
          </select>
        </div>

        <div class="gm-field">
          <label class="gm-label">Attributes:</label>
          <div class="checkbox-group">
            <label>
              <input
                  v-model="selectedAttributes"
                  :value="GodInterventionAttribute.HEALTH"
                  type="checkbox"
              >
              Health
            </label>
            <label>
              <input
                  v-model="selectedAttributes"
                  :value="GodInterventionAttribute.ENERGY"
                  type="checkbox"
              >
              Energy
            </label>
            <label>
              <input
                  v-model="selectedAttributes"
                  :value="GodInterventionAttribute.ACTION_POINTS"
                  type="checkbox"
              >
              Action Points
            </label>
          </div>
        </div>

        <div class="dialog-buttons">
          <button
              :disabled="!isCustomInterventionValid || isLoading"
              class="gm-btn gm-btn--primary apply-button"
              @click="handleCustomIntervention"
          >
            Apply
          </button>
          <button class="gm-btn gm-btn--ghost cancel-button" @click="showCustomDialog = false">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  gameMasterInterventionalService,
  GodInterventionAttribute,
  GodInterventionSize,
  GodInterventionType
} from '@/services/GameMasterInterventionalService.js';
import {gameMasterCharacterService} from '@/services/GameMasterCharacterService.js';

export default {
  name: 'GodInterventionPanel',
  props: {
    characterId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      message: '',
      messageType: 'success',
      showCustomDialog: false,
      GodInterventionType,
      GodInterventionSize,
      GodInterventionAttribute,
      customIntervention: {
        type: GodInterventionType.BLESSING,
        size: GodInterventionSize.MEDIUM
      },
      selectedAttributes: [GodInterventionAttribute.HEALTH]
    };
  },
  computed: {
    isCustomInterventionValid() {
      return this.selectedAttributes.length > 0;
    }
  },
  created() {
    // Set up event listeners
    gameMasterInterventionalService.on('loadingStarted', this.onLoadingStarted);
    gameMasterInterventionalService.on('loadingFinished', this.onLoadingFinished);
    gameMasterInterventionalService.on('interventionSuccess', this.onInterventionSuccess);
    gameMasterInterventionalService.on('interventionFailed', this.onInterventionFailed);
  },
  beforeUnmount() {
    // Clean up event listeners
    gameMasterInterventionalService.off('loadingStarted', this.onLoadingStarted);
    gameMasterInterventionalService.off('loadingFinished', this.onLoadingFinished);
    gameMasterInterventionalService.off('interventionSuccess', this.onInterventionSuccess);
    gameMasterInterventionalService.off('interventionFailed', this.onInterventionFailed);
  },
  methods: {
    // Event handlers
    onLoadingStarted(characterId) {
      if (characterId === this.characterId) {
        this.isLoading = true;
      }
    },

    onLoadingFinished(characterId) {
      if (characterId === this.characterId) {
        this.isLoading = false;
      }
    },

    onInterventionSuccess({characterId, type}) {
      if (characterId === this.characterId) {
        this.showMessage(`${this.getInterventionTypeName(type)} applied successfully!`, 'success');
        this.showCustomDialog = false;

        // Refresh character data
        gameMasterCharacterService.refreshCharacter(this.characterId);
      }
    },

    onInterventionFailed({characterId, type, error}) {
      if (characterId === this.characterId) {
        this.showMessage(`Failed to apply ${this.getInterventionTypeName(type)}: ${error.message || 'Unknown error'}`, 'error');
      }
    },

    // Action handlers
    async handleInstantKill() {
      try {
        await gameMasterInterventionalService.instantKill(this.characterId);
      } catch (error) {
        // Error is already handled by event listener
      }
    },

    async handleInstantHeal() {
      try {
        await gameMasterInterventionalService.instantHeal(this.characterId);
      } catch (error) {
        // Error is already handled by event listener
      }
    },

    async handleCustomIntervention() {
      try {
        const interventionDetails = {
          type: this.customIntervention.type,
          size: this.customIntervention.size,
          attributes: this.selectedAttributes
        };

        await gameMasterInterventionalService.customIntervention(this.characterId, interventionDetails);
      } catch (error) {
        // Error is already handled by event listener
      }
    },

    // Helper methods
    showMessage(message, type = 'success') {
      this.message = message;
      this.messageType = type;

      // Clear message after 3 seconds
      setTimeout(() => {
        this.message = '';
      }, 3000);
    },

    getInterventionTypeName(type) {
      const typeNames = {
        'kill': 'Instant Kill',
        'heal': 'Instant Heal',
        'custom': 'Custom Intervention'
      };

      return typeNames[type] || 'Intervention';
    }
  }
};
</script>

<style scoped>
.god-intervention-panel {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-xs);
  padding: var(--gm-gap-xs) var(--gm-gap-sm);
  border-top: 1px solid var(--gm-border-dim);
  margin-top: var(--gm-gap-xs);
}

.intervention-controls {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--gm-gap-sm);
}

.panel-label {
  margin-right: var(--gm-gap-xs);
}

.intervention-button {
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
}

.heal-button {
  border-color: rgba(143, 182, 60, 0.4);
  color: var(--gm-success);
}

.heal-button:hover:not(:disabled) {
  background: rgba(143, 182, 60, 0.14);
  border-color: var(--gm-success);
}

.custom-button {
  border-color: rgba(127, 168, 201, 0.4);
  color: var(--gm-info);
}

.custom-button:hover:not(:disabled) {
  background: rgba(127, 168, 201, 0.14);
  border-color: var(--gm-info);
}

.intervention-icon {
  font-size: 0.9rem;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.message {
  text-align: center;
  padding: 0.2rem;
  border-radius: var(--gm-radius-sm);
  font-size: 0.7rem;
  color: var(--gm-text);
}

.message.success {
  background: rgba(143, 182, 60, 0.1);
  border-left: 2px solid var(--gm-success);
}

.message.error {
  background: rgba(201, 69, 63, 0.1);
  border-left: 2px solid var(--gm-danger);
}

/* Custom Dialog */
.custom-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding-bottom: var(--gm-dock-clearance);
}

.custom-dialog {
  width: 17.5rem;
  max-width: 90vw;
}

.dialog-title {
  margin-bottom: var(--gm-gap-sm);
  text-align: center;
  padding-bottom: var(--gm-gap-sm);
  border-bottom: 1px solid var(--gm-border-dim);
}

.gm-field {
  margin-bottom: var(--gm-gap-sm);
}

.radio-group, .checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
  padding: var(--gm-gap-xs);
  border: 1px solid var(--gm-border-dim);
}

.radio-group label, .checkbox-group label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: normal;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--gm-text);
}

.dialog-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: var(--gm-gap);
  gap: var(--gm-gap-sm);
}

.apply-button, .cancel-button {
  flex: 1;
}
</style>
